import {invalidateOnSuccess, metadaApi} from '@dms/api/base';
import {BaseVoidResponseSchema} from '@dms/api/shared';

import {
  GetServiceCasesByBusinessCaseApiArg,
  GetServiceCasesByBusinessCaseApiArgSchema,
  GetServiceCasesByBusinessCaseApiResponse,
  GetServiceCasesByBusinessCaseApiResponseSchema,
  PostServiceCasesByBusinessCaseApiArg,
  PostServiceCasesByBusinessCaseApiArgSchema,
  PostServiceCasesByBusinessCaseApiResponse,
  PostSalesServiceOrderVariantSetAsActiveApiArg,
  PostSalesServiceOrderVariantSetAsActiveApiArgSchema,
  PostSalesServiceOrderVariantSetAsActiveApiResponse,
  PostSalesServiceOrderVariantSetAsDefaultApiArg,
  PostSalesServiceOrderVariantSetAsDefaultApiArgSchema,
  PostSalesServiceOrderVariantSetAsDefaultApiResponse,
  PostSalesServiceOrderVariantSetAsInactiveApiArg,
  PostSalesServiceOrderVariantSetAsInactiveApiArgSchema,
  PostSalesServiceOrderVariantSetAsInactiveApiResponse,
  PostSalesServiceOrderVariantUnsetAsDefaultApiArg,
  PostSalesServiceOrderVariantUnsetAsDefaultApiArgSchema,
  PostSalesServiceOrderVariantUnsetAsDefaultApiResponse,
} from './types';

export const businessSalesApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getServiceCasesByBusinessCase: build.query<
      GetServiceCasesByBusinessCaseApiResponse,
      GetServiceCasesByBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/integration/v1/business-case/${queryArg.businessCaseId}/service-cases`,
        queryArg,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'businessCaseServiceCases', id: queryArg.businessCaseId},
        {type: 'businessCaseServiceCases'},
      ],
      extraOptions: {
        responseSchema: GetServiceCasesByBusinessCaseApiResponseSchema,
        requestSchema: GetServiceCasesByBusinessCaseApiArgSchema,
      },
    }),
    postServiceCasesByBusinessCase: build.mutation<
      PostServiceCasesByBusinessCaseApiResponse,
      PostServiceCasesByBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/integration/v1/business-case/${queryArg.businessCaseId}/service-cases`,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PostServiceCasesByBusinessCaseApiArg) => [
          {type: 'businessCaseServiceCases', id: queryArg.businessCaseId},
        ]
      ),
      extraOptions: {
        responseSchema: BaseVoidResponseSchema,
        requestSchema: PostServiceCasesByBusinessCaseApiArgSchema,
      },
    }),
    postSalesServiceOrderVariantSetAsActive: build.mutation<
      PostSalesServiceOrderVariantSetAsActiveApiResponse,
      PostSalesServiceOrderVariantSetAsActiveApiArg
    >({
      query: (queryArg) => ({
        url: '/integration/v1/business-case/service-order-variants/activate',
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['serviceOrderVariants'],
      extraOptions: {
        responseSchema: BaseVoidResponseSchema,
        requestSchema: PostSalesServiceOrderVariantSetAsActiveApiArgSchema,
      },
    }),
    postSalesServiceOrderVariantSetAsInactive: build.mutation<
      PostSalesServiceOrderVariantSetAsInactiveApiResponse,
      PostSalesServiceOrderVariantSetAsInactiveApiArg
    >({
      query: (queryArg) => ({
        url: '/integration/v1/business-case/service-order-variants/deactivate',
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['serviceOrderVariants'],
      extraOptions: {
        responseSchema: BaseVoidResponseSchema,
        requestSchema: PostSalesServiceOrderVariantSetAsInactiveApiArgSchema,
      },
    }),
    postSalesServiceOrderVariantSetAsDefault: build.mutation<
      PostSalesServiceOrderVariantSetAsDefaultApiResponse,
      PostSalesServiceOrderVariantSetAsDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `/integration/v1/business-case/service-order-variants/${queryArg.serviceOrderVariantId}/set-default`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: ['serviceOrderVariants'],
      extraOptions: {
        responseSchema: BaseVoidResponseSchema,
        requestSchema: PostSalesServiceOrderVariantSetAsDefaultApiArgSchema,
      },
    }),
    postSalesServiceOrderVariantUnsetAsDefault: build.mutation<
      PostSalesServiceOrderVariantUnsetAsDefaultApiResponse,
      PostSalesServiceOrderVariantUnsetAsDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `/integration/v1/business-case/service-order-variants/${queryArg.serviceOrderVariantId}/unset-default`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: ['serviceOrderVariants'],
      extraOptions: {
        responseSchema: BaseVoidResponseSchema,
        requestSchema: PostSalesServiceOrderVariantUnsetAsDefaultApiArgSchema,
      },
    }),
  }),
});

export const {
  useGetServiceCasesByBusinessCaseQuery,
  usePostServiceCasesByBusinessCaseMutation,
  usePostSalesServiceOrderVariantSetAsActiveMutation,
  usePostSalesServiceOrderVariantSetAsInactiveMutation,
  usePostSalesServiceOrderVariantSetAsDefaultMutation,
  usePostSalesServiceOrderVariantUnsetAsDefaultMutation,
} = businessSalesApi;
